/**
 * Shared widget data-contract framework.
 *
 * Extracted from the PlantLayoutWidget + PlantLayoutConfigForm pattern.
 * Every module-owned widget should use these helpers so behaviour stays
 * consistent across the long tail of widgets (Admin, CRM, Procurement,
 * Marketplace, Compliance, etc.).
 *
 * What you get:
 *
 *   useWidgetData<T>(config)       - 5-state discriminated union over the
 *                                    underlying useApiQuery + cache + simulator
 *                                    fallback
 *   WidgetCard                     - consistent shell (title bar, module
 *                                    badge, state badge, drilldown chevron,
 *                                    action menu, body slot)
 *   WidgetStateView                - renders the right view per state
 *   createWidgetDescriptor(input)  - fills sensible defaults on the
 *                                    WidgetDescriptor shape so module
 *                                    manifests stay terse
 *
 * Non-goal: this framework does NOT manage the realtime topic subscription
 * fanout (lives in @tyndo/hooks/useSignalR). Widgets that want realtime
 * still take a topic + useRealtime() and pass the result here as `data`.
 */

import * as React from 'react';
import { useApiQuery, type ApiError, extractErrorMessage } from '@tyndo/hooks';
import type {
  WidgetDescriptor,
  WidgetDataSource,
  WidgetSize,
  WidgetPermission,
  WidgetAction,
} from '@tyndo/types';
import {
  __cacheRead as cacheRead,
  __cacheWrite as cacheWrite,
  __resetWidgetCacheForTests,
  type WidgetCachePolicy,
  type WidgetDataState,
  type WidgetDataSourceFlavor,
} from './widget-descriptor';

// Re-export the pure-logic types + helpers so the public surface from
// this file is unchanged for consumers (and the pure file remains
// independently testable without React).
export type {
  WidgetDataState,
  WidgetDataSourceFlavor,
  WidgetCachePolicy,
} from './widget-descriptor';
export {
  __resetWidgetCacheForTests,
  createWidgetDescriptor,
  type CreateWidgetDescriptorInput,
} from './widget-descriptor';

// ────────────────────────────────────────────────────────────────────────
// useWidgetData<T> — main hook every widget calls.
// ────────────────────────────────────────────────────────────────────────

export interface UseWidgetDataConfig<TLive, TProjected = TLive> {
  /** Stable widget id — used as the cache key. */
  widgetId: string;
  /** The descriptor's dataSource block (endpoint, simulator scenario, cache TTL). */
  dataSource: WidgetDataSource | undefined;
  /** Cache policy — defaults to memory. */
  cachePolicy?: WidgetCachePolicy;
  /** True when the user does NOT hold the widget's read permission. */
  permissionDenied?: boolean;
  /** Polling override. Defaults to dataSource.pollMs when set. */
  pollMs?: number;
  /**
   * Treat the response as empty when this predicate is true. Defaults to
   * "null / undefined / [] / {}". Module widgets may override (e.g. when
   * the response is wrapped in `{ items: [] }`).
   */
  isEmpty?: (data: TLive | null | undefined) => boolean;
  /**
   * Optional projection used both for the live response and the simulator
   * fallback. Lets the widget take the same shape from either source.
   */
  project?: (data: TLive | null | undefined, opts: { source: WidgetDataSourceFlavor }) => TProjected;
  /**
   * Simulator-trace projector. When the live endpoint 404s and the
   * dataSource declares a `simulatorScenario`, the hook will swap in the
   * scenario's evidence trace and pass it here. If unset the simulator
   * payload is passed straight through.
   */
  projectSimulator?: (trace: unknown) => TLive;
  /** True if you want to skip the fetch entirely (used by the test harness). */
  skip?: boolean;
}

const DEFAULT_IS_EMPTY = (data: unknown): boolean => {
  if (data == null) return true;
  if (Array.isArray(data)) return data.length === 0;
  if (typeof data === 'object') return Object.keys(data as object).length === 0;
  return false;
};

/**
 * Wraps useApiQuery with cache + simulator fallback + state mapping.
 *
 * Surfaced states (discriminated union):
 *
 *   { status: 'loading' }
 *   { status: 'empty' }
 *   { status: 'error', error }
 *   { status: 'permission-denied' }
 *   { status: 'ready', data, source }
 */
export function useWidgetData<TLive, TProjected = TLive>(
  config: UseWidgetDataConfig<TLive, TProjected>,
): WidgetDataState<TProjected> & { refetch: () => void } {
  const {
    widgetId,
    dataSource,
    cachePolicy = 'memory',
    permissionDenied,
    isEmpty = DEFAULT_IS_EMPTY,
    project,
    projectSimulator,
    skip,
  } = config;

  const endpointPath = dataSource?.endpoint?.path;
  const simulatorScenario = dataSource?.simulatorScenario;
  const pollMs = config.pollMs ?? dataSource?.pollMs;
  const cacheKey = React.useMemo(
    () => `widget:${widgetId}:${endpointPath ?? simulatorScenario ?? 'none'}`,
    [widgetId, endpointPath, simulatorScenario],
  );

  // Normalize the endpoint path — useApiQuery strips a leading /api/vN
  // but module manifests carry the full /api/v1/... shape, so handle both.
  const queryPath = React.useMemo(() => {
    if (!endpointPath) return '';
    if (endpointPath.startsWith('http')) return endpointPath;
    return endpointPath;
  }, [endpointPath]);

  const enabled = !skip && !permissionDenied && Boolean(queryPath);

  const live = useApiQuery<TLive | null>(queryPath, {
    enabled,
    refetchInterval: pollMs && pollMs > 0 ? pollMs : false,
    // Quiet retries — widgets fall back to simulator/cache rather than
    // blocking on slow recoveries.
    retry: 1,
  });

  const [simulatorData, setSimulatorData] = React.useState<TLive | null>(null);
  const [simulatorTried, setSimulatorTried] = React.useState(false);

  // When live errors with a 404 + we have a simulator scenario, try the
  // simulator bridge once. The bridge exposes scenarios under
  // /api/v1/simulator/scenarios/{code}/evidence.
  React.useEffect(() => {
    if (!enabled) return;
    if (!simulatorScenario) return;
    if (simulatorTried) return;
    const err = live.error as ApiError | null;
    const is404 = err?.statusCode === 404;
    if (!is404) return;
    setSimulatorTried(true);
    void (async () => {
      try {
        const baseUrl =
          (typeof window !== 'undefined' && (window as { __TYNDO_API_BASE__?: string }).__TYNDO_API_BASE__) ||
          '/api/v1';
        const res = await fetch(
          `${baseUrl}/simulator/scenarios/${encodeURIComponent(simulatorScenario)}/evidence`,
          { headers: { 'Content-Type': 'application/json' } },
        );
        if (!res.ok) return;
        const json = (await res.json().catch(() => null)) as unknown;
        const projected = projectSimulator
          ? projectSimulator(json)
          : ((json as unknown) as TLive);
        setSimulatorData(projected);
      } catch {
        /* simulator unavailable — fall through to error state */
      }
    })();
  }, [enabled, simulatorScenario, simulatorTried, live.error, projectSimulator]);

  // Pull cached value once on mount.
  const [cachedFallback] = React.useState<TLive | null>(() => {
    const hit = cacheRead(cachePolicy, cacheKey);
    return (hit as TLive | null) ?? null;
  });

  // Persist successful live fetches to cache.
  React.useEffect(() => {
    if (!enabled) return;
    if (live.data == null) return;
    if (isEmpty(live.data as TLive | null)) return;
    cacheWrite(cachePolicy, cacheKey, live.data);
  }, [enabled, live.data, cacheKey, cachePolicy, isEmpty]);

  const state = React.useMemo<WidgetDataState<TProjected>>(() => {
    if (permissionDenied) return { status: 'permission-denied' };
    if (skip) return { status: 'loading' };
    if (!queryPath) {
      return {
        status: 'error',
        error: { message: 'Widget descriptor is missing dataSource.endpoint.' },
      };
    }
    // Live data path.
    if (live.isLoading && !simulatorData && !cachedFallback) {
      return { status: 'loading' };
    }
    if (live.data != null) {
      if (isEmpty(live.data as TLive | null)) return { status: 'empty' };
      const projected = (project
        ? project(live.data as TLive | null, { source: 'live' })
        : (live.data as unknown as TProjected));
      return { status: 'ready', data: projected, source: 'live' };
    }
    // Live failed — try simulator first.
    if (simulatorData != null) {
      if (isEmpty(simulatorData)) return { status: 'empty' };
      const projected = (project
        ? project(simulatorData, { source: 'simulator' })
        : (simulatorData as unknown as TProjected));
      return { status: 'ready', data: projected, source: 'simulator' };
    }
    // Then fall back to cache.
    if (cachedFallback != null) {
      if (isEmpty(cachedFallback)) return { status: 'empty' };
      const projected = (project
        ? project(cachedFallback, { source: 'cached' })
        : (cachedFallback as unknown as TProjected));
      return { status: 'ready', data: projected, source: 'cached' };
    }
    // Permission errors from the API.
    if (live.error) {
      const err = live.error as ApiError;
      if (err.statusCode === 401 || err.statusCode === 403) {
        return { status: 'permission-denied' };
      }
      // 404 with simulator scenario — keep loading while simulator tries.
      if (err.statusCode === 404 && simulatorScenario && !simulatorTried) {
        return { status: 'loading' };
      }
      return {
        status: 'error',
        error: { message: extractErrorMessage(err), statusCode: err.statusCode },
      };
    }
    return { status: 'empty' };
  }, [
    permissionDenied,
    skip,
    queryPath,
    live.isLoading,
    live.data,
    live.error,
    simulatorData,
    simulatorTried,
    simulatorScenario,
    cachedFallback,
    project,
    isEmpty,
  ]);

  // Refetch handle — used by the WidgetCard "retry" button + the action menu.
  const refetch = React.useCallback(() => {
    void live.refetch();
    setSimulatorTried(false);
    setSimulatorData(null);
  }, [live]);

  return { ...state, refetch } as WidgetDataState<TProjected> & { refetch: () => void };
}

// Re-export types other widget modules may want to import alongside the hook.
export type { WidgetDescriptor, WidgetDataSource, WidgetSize, WidgetPermission, WidgetAction };
